import { format } from "date-fns";

export function catLabel(value) {
	return String(value || "")
		.replace(/_/g, " ")
		.replace(/\b\w/g, (ch) => ch.toUpperCase());
}

export function fmtDate(iso) {
	if (!iso) return "—";
	try {
		const d = new Date(iso);
		if (Number.isNaN(d.getTime())) return "—";
		return format(d, "d MMM yyyy");
	} catch {
		return "—";
	}
}

export function jobStatusKey(status) {
	return String(status || "none").toLowerCase() || "none";
}

/** Job statuses that should use the in-progress / spinner treatment. */
export const RUNNING_WRITING_JOB_STATUSES = new Set([
	"in_progress",
	"processing",
	"running",
	"queued",
	"checking_deploy_feasibility",
]);

export function normalizeWritingJobStatusPill(status) {
	const st = jobStatusKey(status);
	if (RUNNING_WRITING_JOB_STATUSES.has(st)) return "in_progress";
	return st;
}

/** Sentinel for site-wide recommendations (url is null). */
export const SITE_WIDE_URL_FILTER = "__site_wide__";

export function applyRecommendationFilters(items, filters) {
	const list = Array.isArray(items) ? items : [];
	const titleQ = String(filters.title || "").trim().toLowerCase();
	return list.filter((it) => {
		if (titleQ && !String(it.title || "").toLowerCase().includes(titleQ)) {
			return false;
		}
		if (filters.category && it.category !== filters.category) return false;
		if (filters.severity) {
			if (filters.severity === "__none__") {
				if (it.severity) return false;
			} else if (it.severity !== filters.severity) {
				return false;
			}
		}
		if (filters.impact && it.impact !== filters.impact) return false;
		if (filters.jobstatus) {
			const st = normalizeWritingJobStatusPill(it.writing_job_status);
			if (st !== filters.jobstatus) return false;
		}
		if (filters.auditId) {
			if (String(it.audit_id || "") !== String(filters.auditId)) return false;
		}
		const pageQ = String(filters.url || "").trim().toLowerCase();
		if (pageQ) {
			if (pageQ === SITE_WIDE_URL_FILTER || pageQ === "site-wide") {
				if (it.url) return false;
			} else {
				const haystack = `${it.page_title || ""} ${it.url || ""}`.toLowerCase();
				if (!haystack.includes(pageQ)) return false;
			}
		}
		return true;
	});
}

export function uniqueCategories(items) {
	const set = new Set();
	(items || []).forEach((it) => {
		if (it?.category) set.add(it.category);
	});
	return Array.from(set).sort();
}

export function uniqueAuditIds(items) {
	const set = new Set();
	(items || []).forEach((it) => {
		if (it?.audit_id != null && it.audit_id !== "") {
			set.add(String(it.audit_id));
		}
	});
	return Array.from(set).sort();
}

/** Unique affected pages from list items. Site-wide (null url) included when present. */
export function uniqueAffectedPages(items) {
	const byUrl = new Map();
	let hasSiteWide = false;
	(items || []).forEach((it) => {
		if (!it?.url) {
			hasSiteWide = true;
			return;
		}
		const url = String(it.url);
		if (!byUrl.has(url)) {
			byUrl.set(url, {
				url,
				page_title: it.page_title || url,
			});
		}
	});
	const pages = Array.from(byUrl.values()).sort((a, b) =>
		String(a.page_title || a.url).localeCompare(String(b.page_title || b.url))
	);
	return { pages, hasSiteWide };
}

export const EMPTY_RECOMMENDATION_FILTERS = {
	title: "",
	category: "",
	severity: "",
	impact: "",
	jobstatus: "",
	url: "",
	auditId: "",
};

/** Map UI filter state → optional API query fields (omit empties except severity None). */
export function recommendationFiltersToApiParams(filters = {}) {
	const params = {};
	const title = String(filters.title || "").trim();
	if (title) params.title = title;
	if (filters.category) params.category = filters.category;
	if (filters.severity === "__none__") {
		params.severity = "";
	} else if (filters.severity) {
		params.severity = filters.severity;
	}
	if (filters.impact) params.impact = filters.impact;
	if (filters.jobstatus) params.jobStatus = filters.jobstatus;
	if (filters.auditId) params.auditId = filters.auditId;
	const pageQ = String(filters.url || "").trim();
	if (pageQ) {
		// Searchword for page_title / url (backend should treat as contains / ILIKE).
		params.url = pageQ;
	}
	return params;
}

export function rowKey(item, index) {
	return `${item?.recommendation_id ?? "x"}-${item?.url ?? "site"}-${index}`;
}

export function canExpand(item) {
	return (
		(Array.isArray(item?.changes) && item.changes.length > 0) ||
		Boolean(item?.writing_instructions)
	);
}

export function canGenerate(item) {
	return Boolean(item?.selectable && !item?.writing_job_id && item?.url);
}

/** Parse writing_instructions markdown into action blocks (HTML mock fallback). */
export function parseInstructionActions(text) {
	const raw = String(text || "");
	const blocks = raw.split(/###\s*Action\s*\d+/i).slice(1);
	if (blocks.length === 0) {
		return [{ fallback: raw }];
	}
	return blocks.map((b, i) => {
		const get = (label) => {
			const m = b.match(new RegExp(`${label}:\\s*(.+)`, "i"));
			return m ? m[1].trim() : null;
		};
		const stripQuotes = (s) => (s ? s.replace(/^"|"$/g, "") : s);
		return {
			index: i + 1,
			operation: get("Operation"),
			htmlTag: get("HTML tag"),
			oldContent: stripQuotes(get("Old content")),
			newContent: stripQuotes(get("Proposed new content")),
			why: get("Why it matters"),
			reasoning: get("Reasoning"),
		};
	});
}

export function normalizeChangesForExpand(item) {
	if (Array.isArray(item?.changes) && item.changes.length > 0) {
		return item.changes.map((c, i) => ({
			index: c.change_index != null ? c.change_index + 1 : i + 1,
			changeIndex: c.change_index != null ? Number(c.change_index) : i,
			operation: c.operation_type || null,
			oldContent: c.old_content || null,
			newContent: c.new_content || null,
			reasoning: c.reasoning || null,
			deploymentStatus: c.deployment_status || null,
			allowEdit: c.allow_edit !== false,
			validationStatus: c.validation_status || null,
			validationExplanation: c.validation_explanation || null,
			why: null,
			htmlTag: null,
		}));
	}
	if (item?.writing_instructions) {
		return parseInstructionActions(item.writing_instructions).map((a, i) => ({
			...a,
			changeIndex: i,
			index: a.index || i + 1,
			deploymentStatus: null,
			validationStatus: null,
			validationExplanation: null,
		}));
	}
	return [];
}

/** Map deploy API status strings onto pill style keys. */
export function deploymentStatusPillKey(status) {
	const st = jobStatusKey(status);
	if (st === "none" || !status) return "none";
	if (st === "failed" || st === "error" || st === "failed_patch") return "failed";
	if (
		st === "completed" ||
		st === "success" ||
		st === "deployed" ||
		st === "overwritten" ||
		st === "reverted"
	)
		return "completed";
	if (st === "pending" || st === "awaiting_review" || st === "in_progress") {
		return st === "in_progress" ? "in_progress" : "pending";
	}
	if (st === "partial" || st === "partially_processed") return "pending";
	if (st === "discarded") return "none";
	return "none";
}

/**
 * Roll up per-change `deployment_status` for the grid column.
 * - no changes / no statuses → null
 * - all same → that status
 * - mixed → { status: "partial", label: "Partial" }
 */
export function rollupDeploymentStatus(item) {
	const changes = Array.isArray(item?.changes) ? item.changes : [];
	const statuses = changes
		.map((c) => String(c?.deployment_status || "").trim())
		.filter(Boolean);

	if (statuses.length === 0) {
		return { status: null, label: null };
	}

	const unique = [...new Set(statuses.map((s) => s.toLowerCase()))];
	if (unique.length === 1) {
		const raw = statuses[0];
		return { status: raw, label: catLabel(raw) };
	}

	return { status: "partial", label: "Partial" };
}

/**
 * Roll up per-change `validation_status` for the grid column.
 * Same rules as deployment status rollup.
 */
export function rollupValidationStatus(item) {
	const changes = Array.isArray(item?.changes) ? item.changes : [];
	const statuses = changes
		.map((c) => String(c?.validation_status || "").trim())
		.filter(Boolean);

	if (statuses.length === 0) {
		return { status: null, label: null };
	}

	const unique = [...new Set(statuses.map((s) => s.toLowerCase()))];
	if (unique.length === 1) {
		const raw = statuses[0];
		return { status: raw, label: catLabel(raw) };
	}

	return { status: "partial", label: "Partial" };
}

/** Reuse deploy pill styles for validation statuses. */
export function validationStatusPillKey(status) {
	return deploymentStatusPillKey(status);
}
